import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { api } from "@/api/client";
import type {
  BoardThickness,
  BoardThicknessCreate,
  BoardThicknessUpdate,
  DataResponse,
} from "@/api/schemas";

const KEY = ["board-thicknesses"] as const;

export function useBoardThicknesses(): UseQueryResult<
  DataResponse<BoardThickness[]>
> {
  return useQuery({
    queryKey: KEY,
    queryFn: async () => {
      const { data } = await api.get<DataResponse<BoardThickness[]>>(
        "/board-thicknesses",
      );
      return data;
    },
    staleTime: 60_000,
  });
}

export function useCreateBoardThickness(): UseMutationResult<
  DataResponse<BoardThickness>,
  Error,
  BoardThicknessCreate
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const { data } = await api.post<DataResponse<BoardThickness>>(
        "/board-thicknesses",
        payload,
      );
      return data;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateBoardThickness(): UseMutationResult<
  DataResponse<BoardThickness>,
  Error,
  { id: string; data: BoardThicknessUpdate }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }) => {
      const r = await api.patch<DataResponse<BoardThickness>>(
        `/board-thicknesses/${id}`,
        data,
      );
      return r.data;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteBoardThickness(): UseMutationResult<
  void,
  Error,
  string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/board-thicknesses/${id}`);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
}
